import React, { useEffect, useState } from "react";
import { IonButton, IonSpinner } from "@ionic/react";
import Textarea from "@mui/joy/Textarea";
import {
  Icon_Thumb_Down_Outline,
  Icon_Thumb_Down_Outline_Filled,
  Icon_Thumb_Up_Outline,
  Icon_Thumb_Up_Outline_Filled,
} from "@assets/images";
import classNames from "classnames";
import { useMutation } from "@tanstack/react-query";
import { PF_SERVER_URL } from "../../Constants";
import { AIMessage } from "@interfaces/AIAssistTypes";
import { IUser } from "@interfaces/Interfaces";
import { useRecoilValueLoadable } from "recoil";
import { userAtom } from "@state/State";

type Props = {
  onSubmitFeedback: () => void;
  visible: boolean;
  sessionId: string;
  messages: AIMessage[];
};

type FeedbackReason = {
  type: "preselected" | "custom";
  value: string;
};

const feedbackOptions = [
  "The dictation didn't capture my reflection well",
  "The evidence wasn't accurate enough",
  "The AI Assist didn't make the process anymore efficient",
];

const FEEDBACK_ANIMATION_DELAY = 1500;
const FEEDBACK_ANIMATION_DURATION = 500;

const getFeedbackValue = (rating: "positive" | "negative" | null) => {
  if (rating === "positive") return 1;
  if (rating === "negative") return 0;
  return null;
};

const feedbackMutationFn = async (params: {
  user: IUser;
  sessionId: string;
  rating: "positive" | "negative" | null;
  reasons: FeedbackReason[];
  other?: string;
  messages: AIMessage[];
}) => {
  const { user, sessionId, rating, reasons, other, messages } = params;

  const sessionID = sessionId;
  const feedback = getFeedbackValue(rating);
  const feedbackReasons: FeedbackReason[] = [
    ...reasons,
    ...(other && other.trim() ? [{ type: "custom" as const, value: other.trim() }] : []),
  ];

  const response = await fetch(`${PF_SERVER_URL}/portfolio/DB/addAIAssistConversation`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      ContactID: user.userData.contactID,
      Token: user.token,
      sessionID,
      messages,
      feedback,
      feedbackReasons: feedbackReasons.length > 0 ? feedbackReasons : null,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to submit feedback");
  }

  return response.json();
};

const AIAssistFeedback: React.FC<Props> = (props) => {
  const user = useRecoilValueLoadable<IUser | null>(userAtom);

  const handleFeedback = useMutation({
    mutationFn: ({
      user,
      sessionId,
      rating,
      reasons,
      other,
      messages,
    }: {
      user: IUser;
      sessionId: string;
      rating: "positive" | "negative" | null;
      reasons: FeedbackReason[];
      other?: string;
      messages: AIMessage[];
    }) => feedbackMutationFn({ user, sessionId, rating, reasons, other, messages }),
    onSuccess: () => {
      setFeedbackSubmitted(true);
      setTimeout(() => {
        setShouldFadeOut(true);
        setShouldAnimate(false);
        setTimeout(() => {
          props.onSubmitFeedback();
        }, FEEDBACK_ANIMATION_DURATION);
      }, FEEDBACK_ANIMATION_DELAY);
    },
    onError: (error) => {
      console.error("feedback submission failed", error);
    },
  });

  const [rating, setRating] = useState<"positive" | "negative" | null>(null);
  const [selectedReasons, setSelectedReasons] = useState<FeedbackReason[]>([]);
  const [otherText, setOtherText] = useState<string>("");
  const [shouldAnimate, setShouldAnimate] = useState<boolean>(false);
  const [shouldFadeOut, setShouldFadeOut] = useState<boolean>(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState<boolean>(false);

  useEffect(() => {
    if (props.visible) {
      setShouldAnimate(false);
      setShouldFadeOut(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setShouldAnimate(true);
        });
      });
    } else {
      setShouldAnimate(false);
      setShouldFadeOut(false);
      // Reset state when component is hidden
      setRating(null);
      setSelectedReasons([]);
      setOtherText("");
      setFeedbackSubmitted(false);
    }
  }, [props.visible]);

  if (!props.visible) {
    return null;
  }

  const toggleReason = (reason: string) => {
    setSelectedReasons((prev) => {
      const existingIndex = prev.findIndex((r) => r.value === reason && r.type === "preselected");
      if (existingIndex >= 0) {
        return prev.filter((_, index) => index !== existingIndex);
      } else {
        return [...prev, { type: "preselected" as const, value: reason }];
      }
    });
  };

  const handleNegativeSubmit = () => {
    handleFeedback.mutate({
      user: user.contents,
      sessionId: props.sessionId,
      rating: "negative",
      reasons: selectedReasons,
      other: otherText.trim() ?? undefined,
      messages: props.messages,
    });
  };

  const handlePositiveSubmit = () => {
    handleFeedback.mutate({
      user: user.contents,
      sessionId: props.sessionId,
      rating: "positive",
      reasons: [],
      other: undefined,
      messages: props.messages,
    });

    setRating("positive");
  };

  const canSubmit = rating !== null && (selectedReasons.length > 0 || otherText.trim().length > 0);

  return (
    <div
      className={classNames("flex justify-center self-center bg-white flex-col overflow-hidden", {
        "rounded-[100px] transition-all ease-in-out": rating !== "negative" || feedbackSubmitted,
        "duration-100": feedbackSubmitted,
        "duration-500": !feedbackSubmitted,
        "rounded-2xl transition-all duration-500 ease-in-out": rating === "negative" && !feedbackSubmitted,
        "max-w-lg": rating !== "negative" || feedbackSubmitted,
        "max-w-full": rating === "negative" && !feedbackSubmitted,
        "max-h-[200px]": rating !== "negative" || feedbackSubmitted,
        "max-h-[1000px]": rating === "negative" && !feedbackSubmitted,
        "ai-message-fade-in": shouldAnimate && !shouldFadeOut,
        "ai-message-fade-out": shouldFadeOut,
        "shadow-[0px_10px_15px_0px_rgba(0,0,0,0.18)] outline outline-1 outline-offset-[-1px] outline-gray-200": rating === "negative" && !feedbackSubmitted,
        "shadow-[0px_4px_10.600000381469727px_0px_rgba(0,0,0,0.25)]": rating !== "negative" || feedbackSubmitted,
      })}
    >
      <div
        className={classNames("flex justify-between items-center gap-4 p-4 px-2 bg-cta-blue", {
          "!px-4": rating === "negative" && !feedbackSubmitted,
          "!px-6": rating !== 'negative' || feedbackSubmitted,
        })}
      >
        <div className="text-white text-acc-16px font-bold">
          {(rating === "positive" || feedbackSubmitted) && "Thanks for your feedback!"}
          {rating === "negative" && !feedbackSubmitted && "Tell us why?"}
          {rating === null && "Was this useful?"}
        </div>
        <div className="flex gap-4">
          <button
            className="size-6"
            disabled={rating !== null}
            onClick={handlePositiveSubmit}
          >
            <img
              src={rating === "positive" ? Icon_Thumb_Up_Outline_Filled : Icon_Thumb_Up_Outline}
              className="size-6 img-no-select"
              alt="Thumbs up"
            />
          </button>
          <button
            className="size-6"
            disabled={rating !== null}
            onClick={() => setRating("negative")}
          >
            <img
              src={rating === "negative" ? Icon_Thumb_Down_Outline_Filled : Icon_Thumb_Down_Outline}
              className="size-6 img-no-select"
              alt="Thumbs down"
            />
          </button>
        </div>
      </div>

      <div
        className={classNames("flex flex-col gap-4 overflow-hidden transition-all ease-in-out", {
          "duration-100": feedbackSubmitted,
          "duration-500": !feedbackSubmitted,
          "max-h-0 max-w-0": rating !== "negative" || feedbackSubmitted,
          "max-h-[1000px] max-w-md": rating === "negative" && !feedbackSubmitted,
        })}
      >
        <div className="flex flex-col gap-3 pt-3.5 px-4">
          {feedbackOptions.map((option) => {
            const isSelected = selectedReasons.some((r) => r.value === option && r.type === "preselected");

            return (
              <div
                key={option}
                className="flex items-center gap-3 cursor-pointer"
                onClick={() => toggleReason(option)}
              >

                <div
                  className={classNames("size-[22px] bg-white/20 rounded-[5px] flex flex-shrink-0 items-center justify-center", {
                    "!bg-cta-blue": isSelected,
                    "border border-gray-300": !isSelected,
                  })}
                >
                  {isSelected && (
                    <div className="text-white font-bold text-sm leading-5">
                      {"✓"}
                    </div>
                  )}
                </div>


                <div
                  className={classNames("text-black text-acc-16px tracking-default font-medium", {
                    "!font-bold !tracking-[-0.37px] ": isSelected,
                  })}
                >
                  {option}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex flex-col gap-2 px-4">
          <div className="text-black text-acc-16px font-acc-500">{"Other"}</div>
          <Textarea
            placeholder="Tell us more..."
            value={otherText}
            onChange={(event) => setOtherText(event.target.value)}
            maxRows={3}
            variant="plain"
            sx={{
              "--Textarea-placeholderOpacity": 0.6,
              "--Textarea-focusedThickness": "0px",
              backgroundColor: "#F9FAFB",
              borderRadius: "5px",
              minHeight: "100px",
              color: "black",
              padding: "12px",
              fontSize: "16px",
              fontWeight: "normal",
              border: "1px solid #CED4DA",
              overflow: "hidden",
              ":hover": {
                color: otherText.length ? "black" : "black", // different color for placeholder text
              },
              "& .MuiTextarea-input": {
                boxSizing: "border-box",
                "&::placeholder": {
                  padding: "10px",
                },
                padding: "8px",
              },
              "--Textarea-focusedInset": "var(--any, )",
              "--Textarea-focusedHighlight": "rgba(255, 255, 255, 0.8)",
              "&:focus-within": {
                borderColor: "#CED4DA !important",
              },
              "&:focus-within::before": {
                boxShadow: "inset 0 0 0 1px",
              },
            }}
          />
        </div>
        <div className="px-4 pb-3.5">
          <IonButton
            mode="ios"
            className="flex w-full !h-10 self-center primary-button"
            onClick={handleNegativeSubmit}
            disabled={!canSubmit || handleFeedback.isPending}
          >
            {handleFeedback.isPending ? (
              <IonSpinner className="h-5 w-5 text-white" />
            ) : (
              <div className="font-bold tracking-default acc-underline text-white">Submit feedback</div>
            )}
          </IonButton>
        </div>
      </div>
    </div>
  );
};

export default AIAssistFeedback;
